const React = require('react')
const { View } = require('react-native')
const L = require('leaflet')

const MapContext = React.createContext(null)

const StyleURL = {
  Light: 'mapbox://styles/mapbox/light-v11',
  Dark: 'mapbox://styles/mapbox/dark-v11',
  Satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
}

function NullComponent() {
  return null
}

const isFiniteNumber = (value) => Number.isFinite(Number(value))

const getChildrenArray = (children) => React.Children.toArray(children)

const getMarkers = (children) =>
  getChildrenArray(children)
    .filter((child) => child?.type?.displayName === 'RNMapboxMarkerView')
    .map((child) => ({
      key: child.key,
      coordinate: child.props.coordinate,
      element: child.props.children,
    }))
    .filter(({ coordinate }) => Array.isArray(coordinate) && coordinate.length >= 2)
    .map((marker) => ({
      ...marker,
      lng: Number(marker.coordinate[0]),
      lat: Number(marker.coordinate[1]),
    }))
    .filter(({ lng, lat }) => isFiniteNumber(lng) && isFiniteNumber(lat))

const getCameraChild = (children) =>
  getChildrenArray(children).find((child) => child?.type?.displayName === 'RNMapboxCamera')

const getInitialBounds = (children, markers) => {
  const cameraChild = getCameraChild(children)
  const bounds = cameraChild?.props?.bounds
  const sw = bounds?.sw || bounds?.southWest
  const ne = bounds?.ne || bounds?.northEast

  if (Array.isArray(sw) && Array.isArray(ne)) {
    const minLng = Number(sw[0])
    const minLat = Number(sw[1])
    const maxLng = Number(ne[0])
    const maxLat = Number(ne[1])
    if ([minLng, minLat, maxLng, maxLat].every(isFiniteNumber)) {
      return [
        [Math.min(minLat, maxLat), Math.min(minLng, maxLng)],
        [Math.max(minLat, maxLat), Math.max(minLng, maxLng)],
      ]
    }
  }

  if (markers.length > 0) {
    return markers.map(({ lat, lng }) => [lat, lng])
  }

  const center = cameraChild?.props?.centerCoordinate
  if (Array.isArray(center) && center.length >= 2) {
    const lng = Number(center[0])
    const lat = Number(center[1])
    if (isFiniteNumber(lng) && isFiniteNumber(lat)) {
      return [
        [lat - 0.02, lng - 0.02],
        [lat + 0.02, lng + 0.02],
      ]
    }
  }

  return [
    [36.8, -9.5],
    [42.3, -6],
  ]
}

const fitMap = (map, boundsLike) => {
  try {
    const bounds = L.latLngBounds(boundsLike).pad(0.18)
    if (bounds.isValid()) {
      map.fitBounds(bounds, { animate: false, padding: [40, 40] })
    }
  } catch {
    map.setView([39.7, -8.1], 8)
  }
}

const tileSources = {
  street: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    options: {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    },
  },
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    options: {
      maxZoom: 19,
      attribution: 'Tiles &copy; Esri',
    },
  },
}

const isSatelliteStyle = (styleURL) => styleURL === StyleURL.Satellite

const makeTileLayer = (styleURL) => {
  const source = isSatelliteStyle(styleURL) ? tileSources.satellite : tileSources.street
  return L.tileLayer(source.url, source.options)
}

const ensureLeafletCss = () => {
  if (typeof document === 'undefined') return
  if (document.querySelector('link[data-findify-leaflet]')) return

  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
  link.dataset.findifyLeaflet = 'true'
  document.head.appendChild(link)

  const style = document.createElement('style')
  style.dataset.findifyLeaflet = 'true'
  style.textContent = `
    .findify-leaflet-map .leaflet-tile-pane {
      filter: saturate(0.62) contrast(0.96) brightness(1.04) sepia(0.06);
      opacity: 0.94;
    }
    .findify-leaflet-map.findify-leaflet-satellite .leaflet-tile-pane {
      filter: saturate(1.1) contrast(1.02) brightness(0.98);
      opacity: 1;
    }
    .findify-leaflet-map .leaflet-control-zoom {
      display: none;
    }
    .findify-leaflet-map .leaflet-control-scale {
      margin-left: 12px;
      margin-top: 10px;
    }
    .findify-leaflet-map .leaflet-control-scale-line {
      height: 12px;
      border: 0;
      border-bottom: 5px solid #000;
      border-left: 5px solid #000;
      border-right: 5px solid #000;
      background: transparent;
      color: #333;
      font-size: 16px;
      line-height: 16px;
      text-shadow: none;
      padding: 0 4px;
    }
    .findify-leaflet-map .leaflet-control-attribution {
      opacity: 0.55;
    }
  `
  document.head.appendChild(style)
}

function MapView({ children, style, styleURL, onCameraChanged }) {
  const containerRef = React.useRef(null)
  const mapRef = React.useRef(null)
  const tileLayerRef = React.useRef(null)
  const markers = React.useMemo(() => getMarkers(children), [children])
  const [mapState, setMapState] = React.useState(null)

  React.useEffect(() => {
    if (!containerRef.current || mapRef.current) return
    ensureLeafletCss()

    const map = L.map(containerRef.current, {
      zoomControl: false,
      attributionControl: true,
    })

    tileLayerRef.current = makeTileLayer(styleURL)
    tileLayerRef.current.addTo(map)
    L.control.scale({ imperial: false, metric: true, position: 'topleft' }).addTo(map)

    mapRef.current = map
    setMapState({ map, version: 0 })
    fitMap(map, getInitialBounds(children, markers))

    const handleMove = () => {
      const center = map.getCenter()
      const event = {
        properties: {
          center: [center.lng, center.lat],
          zoom: map.getZoom(),
          pitch: 0,
          heading: 0,
        },
      }
      setMapState((current) => ({ map, version: (current?.version || 0) + 1 }))
      if (typeof onCameraChanged === 'function') onCameraChanged(event)
    }

    map.on('move zoom', handleMove)
    setTimeout(() => map.invalidateSize(), 0)

    return () => {
      map.off('move zoom', handleMove)
      map.remove()
      mapRef.current = null
    }
  }, [])

  React.useEffect(() => {
    const map = mapRef.current
    if (!map) return

    if (tileLayerRef.current) {
      map.removeLayer(tileLayerRef.current)
    }
    tileLayerRef.current = makeTileLayer(styleURL)
    tileLayerRef.current.addTo(map)
  }, [styleURL])

  React.useEffect(() => {
    if (!mapRef.current) return
    fitMap(mapRef.current, getInitialBounds(children, markers))
  }, [markers.length])

  return React.createElement(
    View,
    {
      style: [
        {
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: '#dceff3',
        },
        style,
      ],
    },
    React.createElement('div', {
      ref: containerRef,
      className: `findify-leaflet-map${isSatelliteStyle(styleURL) ? ' findify-leaflet-satellite' : ''}`,
      style: {
        position: 'absolute',
        inset: 0,
        zIndex: 0,
      },
    }),
    React.createElement(
      MapContext.Provider,
      { value: mapState },
      children
    )
  )
}

function Camera({ centerCoordinate, zoomLevel }, ref) {
  const mapState = React.useContext(MapContext)

  React.useEffect(() => {
    const map = mapState?.map
    if (!map || !Array.isArray(centerCoordinate) || centerCoordinate.length < 2) return

    const lng = Number(centerCoordinate[0])
    const lat = Number(centerCoordinate[1])
    if (!isFiniteNumber(lng) || !isFiniteNumber(lat)) return

    map.setView([lat, lng], isFiniteNumber(zoomLevel) ? Number(zoomLevel) : map.getZoom(), {
      animate: true,
    })
  }, [mapState?.map, centerCoordinate?.[0], centerCoordinate?.[1], zoomLevel])

  React.useImperativeHandle(ref, () => ({
    setCamera: ({ centerCoordinate, zoomLevel }) => {
      const map = mapState?.map
      if (!map || !Array.isArray(centerCoordinate) || centerCoordinate.length < 2) return

      const lng = Number(centerCoordinate[0])
      const lat = Number(centerCoordinate[1])
      if (!isFiniteNumber(lng) || !isFiniteNumber(lat)) return

      map.setView([lat, lng], isFiniteNumber(zoomLevel) ? Number(zoomLevel) : map.getZoom(), {
        animate: true,
      })
    },
  }), [mapState?.map])

  return null
}
const ForwardedCamera = React.forwardRef(Camera)
ForwardedCamera.displayName = 'RNMapboxCamera'

const clampPoint = (value, max) => Math.max(-40, Math.min(max + 40, value))

function MarkerView({ children, coordinate }) {
  const mapState = React.useContext(MapContext)
  const [point, setPoint] = React.useState(null)

  React.useEffect(() => {
    const map = mapState?.map
    if (!map || !Array.isArray(coordinate) || coordinate.length < 2) return

    const lng = Number(coordinate[0])
    const lat = Number(coordinate[1])
    if (!isFiniteNumber(lng) || !isFiniteNumber(lat)) return

    const nextPoint = map.latLngToContainerPoint([lat, lng])
    const size = map.getSize()
    setPoint({
      x: clampPoint(nextPoint.x, size.x),
      y: clampPoint(nextPoint.y, size.y),
    })
  }, [mapState?.version, coordinate?.[0], coordinate?.[1]])

  if (!point) return null

  return React.createElement(
    View,
    {
      style: {
        position: 'absolute',
        left: point.x,
        top: point.y,
        transform: [{ translateX: -12 }, { translateY: -12 }],
        zIndex: 20,
      },
    },
    children
  )
}
MarkerView.displayName = 'RNMapboxMarkerView'

const extractLineCoordinates = (shape) => {
  const geometry = shape?.type === 'Feature' ? shape.geometry : shape
  if (!geometry) return []

  if (geometry.type === 'LineString') {
    return geometry.coordinates || []
  }

  if (geometry.type === 'MultiLineString') {
    return (geometry.coordinates || []).flat()
  }

  if (geometry.type === 'FeatureCollection') {
    return (geometry.features || []).flatMap((feature) => extractLineCoordinates(feature))
  }

  return []
}

function ShapeSource({ shape }) {
  const mapState = React.useContext(MapContext)
  const [points, setPoints] = React.useState([])

  React.useEffect(() => {
    const map = mapState?.map
    if (!map) return

    const nextPoints = extractLineCoordinates(shape)
      .map((coordinate) => [Number(coordinate[0]), Number(coordinate[1])])
      .filter(([lng, lat]) => isFiniteNumber(lng) && isFiniteNumber(lat))
      .map(([lng, lat]) => map.latLngToContainerPoint([lat, lng]))

    setPoints(nextPoints)
  }, [mapState?.version, shape])

  if (points.length < 2) return null

  const path = points
    .map((point) => `${point.x},${point.y}`)
    .join(' ')

  return React.createElement(
    'svg',
    {
      style: {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        zIndex: 15,
        pointerEvents: 'none',
      },
    },
    React.createElement('polyline', {
      points: path,
      fill: 'none',
      stroke: '#233f6b',
      strokeWidth: 5,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      opacity: 0.9,
    }),
    React.createElement('polyline', {
      points: path,
      fill: 'none',
      stroke: '#ffc42d',
      strokeWidth: 2,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      opacity: 0.95,
    })
  )
}
ShapeSource.displayName = 'RNMapboxShapeSource'

function UserLocation({ onUpdate }) {
  React.useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return

    const emitPosition = (position) => {
      if (typeof onUpdate !== 'function') return
      onUpdate({
        coords: {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        },
      })
    }

    navigator.geolocation.getCurrentPosition(emitPosition, () => undefined, {
      enableHighAccuracy: true,
      timeout: 8000,
      maximumAge: 60000,
    })

    const watchId = navigator.geolocation.watchPosition(emitPosition, () => undefined, {
      enableHighAccuracy: true,
      maximumAge: 60000,
    })

    return () => navigator.geolocation.clearWatch(watchId)
  }, [onUpdate])

  return null
}

const RNMapbox = {
  MapView,
  Camera: ForwardedCamera,
  MarkerView,
  PointAnnotation: MarkerView,
  ShapeSource,
  LineLayer: NullComponent,
  SymbolLayer: NullComponent,
  FillLayer: NullComponent,
  UserLocation,
  Logger: { setLogCallback: () => {}, setLogLevel: () => {} },
  setAccessToken: () => {},
  setTelemetryEnabled: () => {},
}

module.exports = {
  __esModule: true,
  default: RNMapbox,
  StyleURL,
  ...RNMapbox,
}
